/**
 * FundSummaryWidget
 *
 * Dashboard widget that displays fund balance summary for donors.
 * Styled to match the donor header with dark blue background.
 */
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Box, Collapse, LinearProgress, Typography } from '@mui/material'

import { queries } from '../../../graphql'
import { Amount, Container, FundSummary, IconButton, Icons, Text, Tooltip } from '../..'

export function FundSummaryWidget({ fundId: fundIdProp } = {}) {
  const { matches: isWideDisplay } = window.matchMedia('(min-width: 600px)')
  const [isOpenByUser, setIsOpenByUser] = useState(null)
  const isOpen = useMemo(() => isOpenByUser ?? isWideDisplay, [isOpenByUser, isWideDisplay])

  const { fundId: fundIdFromParams } = useParams()
  const fundId = fundIdProp ?? fundIdFromParams

  const { data, loading, error } = useQuery(queries.funds.myFund, {
    skip: !fundId,
    variables: { fundId: parseInt(fundId, 10) },
  })

  const fund = data?.me?.fund

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading fund: {error.message}</Typography>
      </Box>
    )
  }

  if (!fund) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No fund data available</Typography>
      </Box>
    )
  }

  const {
    userName,
    name: fundName,
    number: fundNumber,
    fundProfile,
    totalBalance,
    givingWalletBalance,
    investmentWalletBalance,
  } = fund
  const name = fundProfile?.portalName ? fundProfile.portalName : userName

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: 'secondary.main',
        mt: 'calc(-1 * var(--6))',
        py: 3,
      }}
    >
      <Container maxWidth="lg" sx={{ mb: 0 }}>
        <Box sx={{ width: '100%', display: 'flex', flexWrap: 'noWrap' }}>
          <Text.H4 color="secondary.contrastText">{name}</Text.H4>
          <IconButton sx={{ color: 'secondary.contrastText' }} onClick={() => setIsOpenByUser(!isOpen)}>
            {isOpen ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
          </IconButton>
        </Box>
        <Box
          sx={{
            width: '100%',
            color: 'secondary.contrastText',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          {!isOpen && (
            <>
              <Text.Body sx={{ fontSize: '1.25rem' }}>
                <b>
                  {fundName} #{fundNumber}
                </b>
              </Text.Body>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Balance
                  Icon={Icons.Account}
                  amount={totalBalance}
                  tooltip="Represents total value of Giving Wallet plus Investment Account"
                />
                <Balance
                  Icon={Icons.Wallet}
                  amount={givingWalletBalance}
                  tooltip="Represents the value of funds available to give right now"
                />
                <Balance
                  Icon={Icons.Investments}
                  amount={investmentWalletBalance}
                  tooltip="Represents the value of funds that are 'locked' as they are in investment"
                />
              </Box>
            </>
          )}
        </Box>
        <Collapse in={isOpen} unmountOnExit>
          <FundSummary sx={{ mb: 0 }} />
        </Collapse>
      </Container>
    </Box>
  )
}

function Balance({ Icon, amount, tooltip }) {
  return (
    <Tooltip title={tooltip}>
      <Text.Body
        sx={{
          fontSize: '1.25rem',
          color: 'secondary.contrastText',
          display: 'flex',
          mr: 4,
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '4px',
        }}
      >
        <Icon style={{ marginRight: '.5rem', fontSize: '1rem' }} />
        <Amount value={amount} />
      </Text.Body>
    </Tooltip>
  )
}
